/**
 * The top bar in settings mode (the prototype's settings `topbar()` branch): New / Duplicate / Archive,
 * the editor status ("Saved" · "Unsaved changes" · "New item") and the save island (category tools such
 * as Filters' "Test rule", then Save).
 */

import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../icons/Icon.js';
import { Tool } from '../../ui/Tool.js';
import { useServices } from '../../app/services.js';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { showToast } from '../../app/Toast.js';
import { TopbarActions } from '../../app/topbar-slot.js';
import { ui, useUi } from '../../app/ui-store.js';
import type { SettingsCategory, SettingsItem } from './types.js';

/** Provided by the settings layout around the editor; the toolbar renders the element. */
export const SaveToolsSlot = createContext<HTMLElement | null>(null);

/** Extra tools before Save, e.g. `<SaveIslandTools><Tool icon="play" label="Test rule" …/></SaveIslandTools>`. */
export function SaveIslandTools({ children }: { children: ReactNode }) {
  const slot = useContext(SaveToolsSlot);
  return slot ? createPortal(children, slot) : null;
}

/** Run `proceed` now, or after the Keep editing / Discard / Save question when the draft is dirty. */
export function guardDirty(proceed: () => void): void {
  const draft = ui.get().draft;
  if (!draft?.dirty) {
    proceed();
    return;
  }
  openDialog('unsaved-changes', {
    onKeep: () => undefined,
    onDiscard: () => {
      draft.discard();
      proceed();
    },
    onSave: async () => {
      if (await draft.save()) proceed();
    },
  });
}

export function SettingsToolbar({ category, current, saveSlotRef }: { category: SettingsCategory; current: SettingsItem | null; saveSlotRef(element: HTMLElement | null): void }) {
  const navigate = useNavigate();
  const engine = useEngine();
  const services = useServices();
  const queryClient = useQueryClient();
  const draft = useUi((s) => s.draft);
  const noun = category.name.toLowerCase().replace(/s$/, '');
  const archive = category.archive;
  const verb = category.archiveLabel ?? { action: 'Archive', done: 'Item archived' };

  const confirmArchive = (): void => {
    if (!current || !archive) return;
    guardDirty(() =>
      openDialog('confirm', {
        title: `${verb.action} item`,
        message: `${verb.action} ${current.name}?`,
        note: category.archiveNote ?? 'Existing message history and source statistics will remain available.',
        confirmLabel: verb.action,
        onConfirm: async () => {
          try {
            const result = await archive(engine, current, { services, queryClient });
            showToast(verb.done, result.undoId);
            navigate(`/settings/${category.id}`);
          } catch (error) {
            reportError(error);
          }
        },
      }),
    );
  };

  return (
    <TopbarActions>
      <div className="tool-island">
        {category.newLabel ? (
          <>
            <Tool icon="plus" label={category.newLabel || `New ${noun}`} onClick={() => navigate(`/settings/${category.id}/new`)} />
            {category.duplicate ? (
              <Tool
                icon="copy"
                label={current?.duplicable === false ? `This ${noun} cannot be duplicated` : 'Duplicate item'}
                disabled={!current || current.duplicable === false}
                onClick={() => current && navigate(`/settings/${category.id}/new?from=${encodeURIComponent(current.id)}`)}
              />
            ) : null}
            {archive ? (
              <Tool
                icon="trash"
                label={current?.builtin ? `Built-in ${noun} cannot be deleted` : `${verb.action} item`}
                disabled={!current || current.builtin === true}
                onClick={confirmArchive}
              />
            ) : null}
          </>
        ) : null}
      </div>
      <div className={draft?.dirty ? 'editor-status unsaved' : 'editor-status'} id="editor-status" role="status">
        {draft?.dirty ? 'Unsaved changes' : draft?.isNew ? 'New item' : 'Saved'}
      </div>
      <div className="tool-island save-island">
        <span className="save-island-tools" ref={saveSlotRef} />
        <button type="button" className="save-control" onClick={() => void ui.get().draft?.save()}>
          <Icon name="check" />
          <span>Save</span>
        </button>
      </div>
    </TopbarActions>
  );
}
